import posthogClient from "posthog-js";

import { getSessionReplaySampleRate, shouldSampleSessionReplay, startSessionReplay } from "~/analytics/sessionReplay";
import { PROGRESS_EVENT_NAME } from "~/lib/analytics.utils";

import { createPostHogBeforeSend } from "../posthogExceptionFilter";
import type { AnalyticsAdapter } from "../types";

const FEATURE_FLAG_RECOVERY_DELAY_MS = 15_000;

export class PosthogAdapter implements AnalyticsAdapter {
  private authenticatedIdentityActive = false;
  private featureFlagListenerRegistered = false;
  private featureFlagRecoveryTimeout: ReturnType<typeof setTimeout> | null = null;
  private replaySessionId?: string;

  init(): void {
    posthogClient.init(import.meta.env.VITE_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
      defaults: "2025-11-30",
      capture_pageview: false, // We'll handle pageviews manually
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      person_profiles: "identified_only",
      advanced_disable_feature_flags_on_first_load: true,
      disable_conversations: true,
      disable_session_recording: true,
      before_send: createPostHogBeforeSend({
        deploymentEnvironment: import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT,
      }),
    });

    if (!this.featureFlagListenerRegistered) {
      posthogClient.onFeatureFlags((_flags, _variants, context) => {
        if (this.authenticatedIdentityActive && !context?.errorsLoading) {
          this.cancelFeatureFlagRecovery();
        }
      });
      this.featureFlagListenerRegistered = true;
    }
    this.syncSessionReplaySampling();
  }

  page(input: { name?: string; properties?: Record<string, unknown> }): void {
    this.syncSessionReplaySampling();
    posthogClient.capture("$pageview", {
      ...(input.name && { $current_url: input.name }),
      ...input.properties,
    });
  }

  track(input: { event: string; properties?: Record<string, unknown> }): void {
    /**
     *
     * this should not report audio progress to posthog. once mixpanel is removed, this is no longer necessary
     */
    if (input.event === PROGRESS_EVENT_NAME) {
      return;
    }
    posthogClient.capture(input.event, input.properties);
  }

  identify(input: { userId?: string; traits?: Record<string, unknown> }): void {
    if (!input.userId) {
      if (input.traits) {
        posthogClient.setPersonProperties(input.traits);
      }
      return;
    }

    const persistedUserId = posthogClient.get_property("$user_id");
    if (typeof persistedUserId === "string" && persistedUserId !== input.userId) {
      posthogClient.reset();
    }

    this.authenticatedIdentityActive = true;
    this.scheduleFeatureFlagRecovery();
    posthogClient.identify(input.userId, input.traits);
  }

  reset(): void {
    this.authenticatedIdentityActive = false;
    this.cancelFeatureFlagRecovery();
    posthogClient.reset();
  }

  private scheduleFeatureFlagRecovery(): void {
    this.cancelFeatureFlagRecovery();
    this.featureFlagRecoveryTimeout = setTimeout(() => {
      this.featureFlagRecoveryTimeout = null;
      if (this.authenticatedIdentityActive) {
        posthogClient.reloadFeatureFlags();
      }
    }, FEATURE_FLAG_RECOVERY_DELAY_MS);
  }

  private cancelFeatureFlagRecovery(): void {
    if (this.featureFlagRecoveryTimeout !== null) {
      clearTimeout(this.featureFlagRecoveryTimeout);
      this.featureFlagRecoveryTimeout = null;
    }
  }

  private syncSessionReplaySampling(): void {
    const sessionId = posthogClient.get_session_id();
    if (!sessionId || sessionId === this.replaySessionId) return;

    this.replaySessionId = sessionId;
    if (shouldSampleSessionReplay(sessionId, getSessionReplaySampleRate())) {
      startSessionReplay();
    } else {
      posthogClient.stopSessionRecording();
    }
  }
}
